import { FC, RefObject, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useDirection } from "src/i18n/direction";

export type TabSection = {
  key: string;
  label: string;
  ref: RefObject<HTMLDivElement>;
};

const BookingTabs: FC<{ sections: TabSection[] }> = ({ sections }) => {
  const [activeKey, setActiveKey] = useState(sections[0]?.key ?? "");
  const intersectingRef = useRef<Record<string, boolean>>({});
  const scrollTargetKeyRef = useRef<string | null>(null);
  const direction = useDirection();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, axis: "x", align: "start", dragThreshold: 6, inViewThreshold: 0.1, active: true, direction, dragFree: true });

  useEffect(() => {
    if (!emblaApi) return;
    const index = sections.findIndex((s) => s.key === activeKey);
    if (index !== -1) emblaApi.scrollTo(index);
  }, [activeKey, emblaApi, sections]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const key = entry.target.getAttribute("data-section-key");
          if (key) intersectingRef.current[key] = entry.isIntersecting;
        });
        const active = [...sections].reverse().find((s) => intersectingRef.current[s.key]);
        if (!active) return;
        if (scrollTargetKeyRef.current && active.key !== scrollTargetKeyRef.current) return;
        scrollTargetKeyRef.current = null;
        setActiveKey(active.key);
      },
      { rootMargin: "-10px 0px 0px 0px", threshold: .1 },
    );

    sections.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (key: string, ref: RefObject<HTMLDivElement>) => {
    scrollTargetKeyRef.current = key;
    setActiveKey(key);
    ref.current?.scrollIntoView({ behavior: "smooth", inline: "center", block: "start" });
  };

  return (
    <div ref={emblaRef} className="overflow-hidden border-b border-border-default bg-background-default">
      <div className="flex gap-6">
        {sections.map(({ key, label, ref }) => (
          <button
            key={key}
            onClick={() => scrollTo(key, ref)}
            className={`shrink-0 whitespace-nowrap text-sm font-medium transition-colors py-5! ${
              activeKey === key
                ? "border-b-2 border-background-active text-font-active"
                : "text-font-default hover:text-font-active"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default BookingTabs;
